import { Box, Button, Flex } from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ViewDocumentAsOwnerDto } from "../../../autogen/bff-api";
import { CombinedDocuments } from "../../../common/documents/CombinedDocuments";
import {
  IncompleteFileUpload,
  isIncompleteFileUpload,
  UploadCompletedStatus,
} from "../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../common/documents/UppyUploader/useUppyUploader";
import { useAppDispatch } from "../../../common/redux/hooks";
import { loadBasicBidThunk } from "../../../common/redux/thunks/basic-bid/load-basic-bid-thunk";
import { useBasicBidState } from "../../sourcing-events/useBasicBidEventState";

interface Props {
  previousStep: () => void;
  nextStep: () => void;
}

export const Documents = ({ previousStep, nextStep }: Props) => {
  const { t } = useTranslation();
  const bidState = useBasicBidState();
  const dispatch = useAppDispatch();

  const loadBid = useCallback(() => {
    dispatch(loadBasicBidThunk({ bidId: bidState.id }));
  }, [bidState.id, dispatch]);

  const { uppy, allFiles, documents, setDocuments, setUploads, removeUpload } = useUppyUploader({
    entityType: "Bid",
    entityId: bidState.id,
    reloadEntity: loadBid,
  });

  useEffect(() => {
    setDocuments(bidState.documents.value);
  }, [bidState.documents.value, setDocuments]);

  useEffect(() => {
    setUploads(bidState.uploads.value);
  }, [bidState.uploads, setUploads]);

  return (
    <Box pt="4">
      <Box pb="8">
        <CombinedDocuments
          documentUploadConfig={{
            uppy,
            dropTargetId: "bidFileTarget",
            dropTargetHelperText: t("Upload documents relevant for your bid"),
            uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
            removeUpload,
          }}
          uploadedDocuments={documents}
          setUploadedDocuments={setDocuments}
          isTagsColumnVisible={false}
          canDeleteDocuments={true}
          removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
            removeUpload({
              status: UploadCompletedStatus.UploadCompleted,
              documentId: document.id,
              uploadId: document.fromUploadId,
              ...document,
            })
          }
        />
      </Box>
      <Flex pt="8">
        <Button variant={"outline"} mr="5px" w="50%" colorScheme={"teal"} onClick={previousStep}>
          {t("Previous")}
        </Button>
        <Button variant={"solid"} w="50%" colorScheme={"teal"} onClick={nextStep}>
          {t("Next")}
        </Button>
      </Flex>
    </Box>
  );
};
